import { type ReactNode, useEffect, useRef, useState } from 'react';

import type { AssetType } from '@cartwhl/common/api/getAssets';
import PersonIcon from '@cartwhl/ui/icons/PersonIcon';

import { isAssetVideoThumbnail, shouldUpdateStableMediaSrc } from '../utils';

function shouldForceThumbnailCover(assetType: AssetType): boolean {
  return assetType === 'MOGEN_JOB' || assetType === 'MASCOT_JOB';
}

function AssetLibrarySceneFallbackThumbnail({
  className = 'absolute inset-0 h-full w-full',
}: {
  className?: string;
}) {
  return (
    <div
      className={`${className} bg-secondary flex items-center justify-center`}
    >
      <PersonIcon className="text-tertiary h-16 w-16" />
    </div>
  );
}

export interface AssetLibraryThumbnailProps {
  assetType: AssetType;
  thumbnailURL: string | null;
  className?: string;
  isHovered?: boolean;
  autoPlayVideo?: boolean;
  onLoadError?: () => void;
}

export default function AssetLibraryThumbnail({
  assetType,
  thumbnailURL,
  className = 'absolute inset-0 h-full w-full',
  isHovered = false,
  autoPlayVideo = false,
  onLoadError,
}: AssetLibraryThumbnailProps) {
  const candidateUrl = thumbnailURL?.trim() ?? '';
  const isVideo = isAssetVideoThumbnail(assetType, candidateUrl);
  const forceFitCover = shouldForceThumbnailCover(assetType);

  if (!candidateUrl) {
    if (assetType === 'SCENE') {
      return <AssetLibrarySceneFallbackThumbnail className={className} />;
    }

    return null;
  }

  if (isVideo) {
    return (
      <AssetLibraryThumbnailVideo
        assetType={assetType}
        candidateUrl={candidateUrl}
        className={className}
        isHovered={isHovered}
        autoPlayVideo={autoPlayVideo}
        forceFitCover={forceFitCover}
        onLoadError={onLoadError}
      />
    );
  }

  return (
    <AssetLibraryThumbnailImage
      assetType={assetType}
      candidateUrl={candidateUrl}
      className={className}
      forceFitContain={assetType === 'MEDIA_IMAGE'}
      forceFitCover={forceFitCover}
      onLoadError={onLoadError}
    />
  );
}

/** True = fit without cropping; false = landscape cover. */
function shouldFitThumbnailContain(
  width: number,
  height: number,
): boolean | null {
  if (width <= 0 || height <= 0) {
    return null;
  }

  return height >= width;
}

function getThumbnailMediaClassName(
  baseClassName: string,
  shouldFitContain: boolean | null,
): string {
  if (shouldFitContain === false) {
    return `${baseClassName} object-cover object-center`;
  }

  return 'max-h-full w-auto max-w-full object-contain';
}

function AssetLibraryThumbnailFrame({
  shouldFitContain,
  children,
}: {
  shouldFitContain: boolean | null;
  children: ReactNode;
}) {
  return (
    <div
      className={`absolute inset-0 flex items-center justify-center ${
        shouldFitContain !== false ? 'bg-secondary' : ''
      }`}
    >
      {children}
    </div>
  );
}

function AssetLibraryThumbnailImage({
  assetType,
  candidateUrl,
  className,
  forceFitContain = false,
  forceFitCover = false,
  onLoadError,
}: {
  assetType: AssetType;
  candidateUrl: string;
  className: string;
  forceFitContain?: boolean;
  forceFitCover?: boolean;
  onLoadError?: () => void;
}) {
  const imageRef = useRef<HTMLImageElement>(null);
  const [hasLoadError, setHasLoadError] = useState(false);
  const [shouldFitContain, setShouldFitContain] = useState<boolean | null>(
    forceFitCover ? false : forceFitContain ? true : null,
  );

  useEffect(() => {
    setHasLoadError(false);
    setShouldFitContain(
      forceFitCover ? false : forceFitContain ? true : null,
    );
  }, [candidateUrl, forceFitContain, forceFitCover]);

  useEffect(() => {
    const image = imageRef.current;
    if (!image) {
      return;
    }

    const currentSrc = image.currentSrc || image.src || '';
    if (shouldUpdateStableMediaSrc(currentSrc, candidateUrl, hasLoadError)) {
      image.src = candidateUrl;
      setHasLoadError(false);
    }

    if (forceFitCover) {
      setShouldFitContain(false);
      return;
    }

    if (forceFitContain) {
      setShouldFitContain(true);
      return;
    }

    if (image.complete) {
      setShouldFitContain(
        shouldFitThumbnailContain(image.naturalWidth, image.naturalHeight),
      );
    }
  }, [candidateUrl, forceFitContain, forceFitCover, hasLoadError]);

  if (hasLoadError) {
    if (assetType === 'SCENE') {
      return <AssetLibrarySceneFallbackThumbnail className={className} />;
    }

    return null;
  }

  return (
    <AssetLibraryThumbnailFrame shouldFitContain={shouldFitContain}>
      <img
        ref={imageRef}
        alt=""
        className={getThumbnailMediaClassName(className, shouldFitContain)}
        crossOrigin="anonymous"
        onLoad={(event) => {
          if (forceFitCover) {
            setShouldFitContain(false);
            return;
          }

          if (forceFitContain) {
            setShouldFitContain(true);
            return;
          }

          const image = event.currentTarget;
          setShouldFitContain(
            shouldFitThumbnailContain(image.naturalWidth, image.naturalHeight),
          );
        }}
        onError={() => {
          setHasLoadError(true);
          onLoadError?.();
        }}
      />
    </AssetLibraryThumbnailFrame>
  );
}

function AssetLibraryThumbnailVideo({
  assetType,
  candidateUrl,
  className,
  isHovered,
  autoPlayVideo,
  forceFitCover = false,
  onLoadError,
}: {
  assetType: AssetType;
  candidateUrl: string;
  className: string;
  isHovered: boolean;
  autoPlayVideo: boolean;
  forceFitCover?: boolean;
  onLoadError?: () => void;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [hasLoadError, setHasLoadError] = useState(false);
  const [shouldFitContain, setShouldFitContain] = useState<boolean | null>(
    forceFitCover ? false : null,
  );

  const syncFitMode = (video: HTMLVideoElement) => {
    if (forceFitCover) {
      setShouldFitContain(false);
      return;
    }

    setShouldFitContain(
      shouldFitThumbnailContain(video.videoWidth, video.videoHeight),
    );
  };

  useEffect(() => {
    setHasLoadError(false);
    setShouldFitContain(forceFitCover ? false : null);
  }, [candidateUrl, forceFitCover]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) {
      return;
    }

    const currentSrc = video.currentSrc || video.src || '';
    if (shouldUpdateStableMediaSrc(currentSrc, candidateUrl, hasLoadError)) {
      video.src = candidateUrl;
      setHasLoadError(false);
    }

    const handleMetadata = () => {
      syncFitMode(video);
    };

    video.addEventListener('loadedmetadata', handleMetadata);
    if (video.readyState >= HTMLMediaElement.HAVE_METADATA) {
      handleMetadata();
    }

    return () => {
      video.removeEventListener('loadedmetadata', handleMetadata);
    };
  }, [candidateUrl, forceFitCover, hasLoadError]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) {
      return;
    }

    if (autoPlayVideo) {
      void video.play();
      return;
    }

    if (isHovered) {
      void video.play();
      return;
    }

    video.pause();
    video.currentTime = 0;
  }, [autoPlayVideo, isHovered]);

  if (hasLoadError) {
    if (assetType === 'SCENE') {
      return <AssetLibrarySceneFallbackThumbnail className={className} />;
    }

    return null;
  }

  return (
    <AssetLibraryThumbnailFrame shouldFitContain={shouldFitContain}>
      <video
        ref={videoRef}
        className={getThumbnailMediaClassName(className, shouldFitContain)}
        muted
        loop
        playsInline
        preload="metadata"
        crossOrigin="anonymous"
        onLoadedMetadata={(event) => {
          syncFitMode(event.currentTarget);
        }}
        onLoadedData={() => {
          const video = videoRef.current;
          if (!video) {
            return;
          }

          syncFitMode(video);

          if (autoPlayVideo || isHovered) {
            void video.play();
            return;
          }

          video.pause();
          video.currentTime = 0;
        }}
        onError={() => {
          setHasLoadError(true);
          onLoadError?.();
        }}
      />
    </AssetLibraryThumbnailFrame>
  );
}
